import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useSelector } from "react-redux";
import CartProduct from "./CartProduct";
import {
  DEFAULT_CURRENCY,
  freeShippingThreshold,
  getShippingCostPrice,
  qualifiesForFreeShipping,
} from "../../../utils/global_const";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../utils/navigation";
import { useTranslation } from "react-i18next";

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(100%);
  }
`;
const slideInD = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const slideOutD = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;
const ContainerX = styled.div`
  position: relative;
`;
const Container = styled.div`
  margin-top: var(--spacing-xxs);
  background-color: transparent;
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--zindex-default);
  min-width: 100%;
  height: 100vh;

  box-sizing: border-box;
  overflow-x: hidden;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 400ms ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
`;
const Wrapper = styled.div`
  z-index: calc(var(--zindex-default) + 1);
  background-color: var(--bg-100);

  width: 100%;
  height: 100%;
  min-height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${slideIn} 0.4s ease-out forwards; // Duže trajanje za slide-in
        `
      : css`
          animation: ${slideOut} 0.1s ease-in forwards; // Kraće trajanje za slide-out
        `}
  @media (min-width: 768px) {
    position: absolute;
    right: 0%;
    top: calc(100% + 10px);
    width: 400px;
    height: auto;
    min-height: auto;
    //min-height: 200px;
    box-shadow: var(--shadow-large);
    ${({ $isOpen }) =>
      $isOpen
        ? css`
            animation: ${slideInD} 0.4s ease-out forwards; // Duže trajanje za slide-in
          `
        : css`
            animation: ${slideOutD} 0.1s ease-in forwards; // Kraće trajanje za slide-out
          `};
  }
`;

const CartContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 4px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s ease, transform 0.1s ease;
  @media (min-width: 768px) {
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    border-radius: var(--border-radius-base, 8px);
    padding: 0 8px;
    gap: 8px;
  }
  svg {
    fill: none;
    stroke: var(--bg-200);
    stroke-width: 1.9px;
    flex-shrink: 0;
    transition: stroke 0.3s ease; /* Dodaj tranziciju za glatki prelaz boje */
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.14);
  }
  &:active {
    transform: scale(0.95);
  }
  &:focus-visible {
    outline: 2px solid var(--bg-100);
    outline-offset: 2px;
  }
  &:hover svg {
    stroke: var(--bg-100);
  }
  &:hover h4 {
    color: var(--bg-100);
  }
`;
const CartText = styled.h4`
  display: none;
  cursor: pointer;
  white-space: nowrap;
  @media (min-width: 768px) {
    display: inline-block;
  }

  font-weight: 200;
  color: var(--bg-100);
`;
const SumQuantity = styled.div`
  cursor: pointer;
  position: absolute;
  right: -10px;
  top: -4px;

  background-color: var(--primary-200);
  color: var(--bg-100);
  border-radius: var(--border-radius-large);
  padding: 4px 2px;
  margin: 0;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-small);
  font-weight: 200;
`;
const Span = styled.span`
  font-weight: 500;
  font-size: var(--font-size-small);
`;
const XDiv = styled.div`
  padding: 0 var(--spacing-md);
  cursor: pointer;
  height: var(--navbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
  background-color: var(--bg-200);
  color: var(--text-200);
`;
const ShippingBar = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--primary-300);
`;
const ShippingText = styled.p`
  margin: 0 0 6px 0;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--primary-100);
  display: flex;
  align-items: center;
  gap: 6px;
`;
const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: var(--border-radius-large);
  background-color: var(--bg-100);
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-100);
  width: ${(props) => props.$percent}%;
  transition: width 0.4s ease;
`;
const MiddleDiv = styled.div`
  max-height: calc(100vh - var(--navbar-height) * 3 - 30px);
  @media (min-width: 768px) {
    max-height: 400px;
  }
  overflow: auto; /* omogućava skrolanje unutar div-a */
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--text-200);
    border-radius: 5px;
  }
`;

const Logo = styled.div`
  color: var(--text-color);

  font-size: var(--font-size-xlarge);
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.438px;
  display: flex;
  gap: var(--spacing-xs);

  align-items: center;
  cursor: pointer;
`;
const IconDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Bottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(var(--navbar-height) * 2);
  border-top: 1px solid var(--bg-300);
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.15);
  background-color: var(--bg-200);
  @media (min-width: 768px) {
    position: static;
  }
`;

const BottomWrapper = styled.div`
  width: 96%;
  height: calc(100% - 20px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
`;
const FlexDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Button = styled.button`
  text-transform: uppercase;
  //height: 100%;
`;
const Notice = styled.p`
  margin: 16px 0;
  font-style: italic;
  text-align: center;
`;

const CartMenu = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldBeVisible, setShouldBeVisible] = useState(false);
  const [paddingTop, setPaddingTop] = useState(20);
  const { currencyTag, shippingMethod } = useContext(ProductContext);
  const navigate = useNavigate();
  const { goToCheckout } = useNavigation();

  useEffect(() => {
    if (isOpen) {
      setShouldBeVisible(true);
    } else {
      const timer = setTimeout(() => {
        setShouldBeVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const cartItems = useSelector((state) => state.cart.itemsList);
  let totalQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.discount_price * item.quantity,
    0
  );
  const subtotalRounded = parseFloat(subtotal.toFixed(2));
  const savings = cartItems.reduce(
    (totalSavings, item) =>
      totalSavings +
      (item.product.price - item.product.discount_price) * item.quantity,
    0
  );
  const savingsRounded = parseFloat(savings.toFixed(2));
  const [shippingCost, setShippingCost] = useState(0.0);
  const currency = localStorage.getItem("currency") || DEFAULT_CURRENCY;
  useEffect(() => {
    if (qualifiesForFreeShipping(subtotalRounded, shippingMethod.name)) {
      setShippingCost(0);
    } else {
      setShippingCost(
        getShippingCostPrice(shippingMethod.name, currency, DEFAULT_CURRENCY)
      );
    }
  }, [subtotalRounded, shippingMethod]);

  const remainingForFreeShipping = parseFloat(
    Math.max(freeShippingThreshold - subtotalRounded, 0).toFixed(2)
  );
  const freeShippingUnlocked = remainingForFreeShipping === 0;
  const shippingProgressPercent = Math.min(
    (subtotalRounded / freeShippingThreshold) * 100,
    100
  );

  const grandTotal = subtotalRounded + shippingCost;
  const grandTotalRounded = parseFloat(grandTotal.toFixed(2));

  const inStockItems = cartItems.filter(
    (item) => item.product.is_in_stock === "in_stock"
  );
  const otherItems = cartItems.filter(
    (item) => item.product.is_in_stock !== "in_stock"
  );

  return (
    <ContainerX>
      <CartContainer
        onClick={toggleMenu}
        role="button"
        tabIndex={0}
        aria-label={isOpen ? t("ARIA.CLOSE_CART") : t("ARIA.OPEN_CART")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMenu();
          }
        }}
      >
        <svg
          width="24px"
          height="24px"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.75 3h2.1l.66 3M5.51 6H21l-1.85 8.32a1.75 1.75 0 01-1.71 1.36H8.35a1.75 1.75 0 01-1.71-1.36L5.51 6z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="9.75" cy="20.5" r="1.15" />
          <circle cx="18.25" cy="20.5" r="1.15" />
        </svg>
        {totalQuantity !== 0 && <SumQuantity>{totalQuantity}</SumQuantity>}
        <CartText>{t("CART.TITLE")}</CartText>
      </CartContainer>

      <Container
        onClick={toggleMenu}
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
      />
      <Wrapper
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <XDiv>
          <Logo>
            {totalQuantity} {t("CART.ITEMS")}
          </Logo>
          <IconDiv>
            <svg
              width="30px"
              height="30px"
              viewBox="0 0 1024 1024"
              xmlns="http://www.w3.org/2000/svg"
              onClick={toggleMenu}
              role="button"
              tabIndex={0}
              aria-label={t("ARIA.CLOSE_CART")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleMenu();
                }
              }}
            >
              <path
                fill="var(--text-200)"
                d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"
              />
            </svg>
          </IconDiv>
        </XDiv>
        {totalQuantity > 0 && (
          <ShippingBar>
            <ShippingText>
              {freeShippingUnlocked
                ? `🎉 ${t("CART.FREE_SHIPPING_UNLOCKED")}`
                : t("CART.FREE_SHIPPING_PROGRESS", {
                    amount: `${currencyTag}${remainingForFreeShipping.toFixed(2)}`,
                  })}
            </ShippingText>
            <ProgressTrack>
              <ProgressFill $percent={shippingProgressPercent} />
            </ProgressTrack>
          </ShippingBar>
        )}
        <MiddleDiv $paddingTop={`${paddingTop}px`}>
          {inStockItems.map((item, index) => (
            <CartProduct item={item} key={index} />
          ))}
          {otherItems.length > 0 && (
            <>
              <Notice>{t("CART.NOTICE")}</Notice>
              {otherItems.map((item, idx) => (
                <CartProduct item={item} key={`out-${idx}`} />
              ))}
            </>
          )}
        </MiddleDiv>
        <Bottom>
          <BottomWrapper>
            <FlexDiv>
              <h3>{t("CART.SHOPPING_BASKET")}</h3>
              <h3>
                {currencyTag}
                {grandTotalRounded}
              </h3>
            </FlexDiv>
            {/* <FlexDiv>
              <p style={{ padding: "0", margin: "0" }}>{t("CART.VAT")}</p>
              <p style={{ padding: "0", margin: "0" }}>{t("CART.INCLUDED")}</p> 
            </FlexDiv> */}
            <Button
              onClick={() => navigate(goToCheckout())}
              disabled={totalQuantity == 0}
            >
              {t("CART.CHECKOUT")}
            </Button>
          </BottomWrapper>
        </Bottom>
      </Wrapper>
    </ContainerX>
  );
};

export default CartMenu;
